
import { newWebSocketRpcSession } from 'capnweb';
import { createMain } from "./worker.js";

const WS_PATH = "/ws";
const ENV = {};

const NativeWebSocket = globalThis.WebSocket;

/** One end of an in-page pair. Implements just enough of the WebSocket API. */
class PairedSocket extends EventTarget {
  peer = null;
  binaryType = 'blob';
  readyState = 0; // CONNECTING

  constructor(url) {
    super();
    this.url = url;
  }

  send(data) {
    if (this.readyState !== 1) throw new DOMException('Socket is not open', 'InvalidStateError');
    const peer = this.peer;
    // Asynchronous, so a send can never re-enter the sender synchronously --
    // which is what a real socket guarantees and what the RPC layer expects.
    queueMicrotask(() => {
      if (peer.readyState === 1) peer.dispatchEvent(new MessageEvent('message', { data }));
    });
  }

  close(code = 1000, reason = '') {
    if (this.readyState > 1) return;
    const both = [this, this.peer];
    for (const side of both) side.readyState = 2; // CLOSING
    queueMicrotask(() => {
      for (const side of both) {
        side.readyState = 3; // CLOSED
        side.dispatchEvent(new CloseEvent('close', { code, reason, wasClean: code === 1000 }));
      }
    });
  }
}

function connectedPair(url) {
  const client = new PairedSocket(url);
  const server = new PairedSocket(url);
  client.peer = server;
  server.peer = client;

  // Open on a task boundary, mirroring a real connection: the caller gets the
  // socket back in CONNECTING and the transport queues sends until 'open'.
  setTimeout(() => {
    for (const side of [client, server]) {
      side.readyState = 1; // OPEN
      side.dispatchEvent(new Event('open'));
    }
  }, 0);

  return { client, server };
}

function Shim(url, protocols) {
  if (new URL(url, location.href).pathname !== WS_PATH) {
    return new NativeWebSocket(url, protocols);
  }
  const { client, server } = connectedPair(String(url));
  newWebSocketRpcSession(server, createMain(ENV));
  return client;
}

// The transport reads WebSocket.CONNECTING off the constructor, so the
// readyState constants have to survive the swap.
for (const [name, value] of [['CONNECTING', 0], ['OPEN', 1], ['CLOSING', 2], ['CLOSED', 3]]) {
  Shim[name] = value;
}

globalThis.WebSocket = Shim;
